import React, { useState, Suspense, useEffect } from 'react';
import { View, SEONewsItem, COSTS } from './types';
import Layout from './components/Layout';
import Login from './components/Login';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider, useToast } from './contexts/ToastContext';
import { QueueProvider } from './contexts/QueueContext';
import { db } from './services/firebase';
import { fetchRealtimeSEONews } from './services/geminiService';
import { 
    Sparkles, Search, Zap, Swords, PenTool, Loader2, 
    Wallet as WalletIcon, HardDrive, Globe, Activity, 
    ChevronRight, Command, LayoutGrid, UsersRound, Bot,
    TrendingUp, Lightbulb, AlertTriangle, BarChart3, BrainCircuit, RefreshCw, Target
} from 'lucide-react';

// Lazy loaded components
const Writer = React.lazy(() => import('./components/Writer'));
const Audit = React.lazy(() => import('./components/Audit'));
const Tools = React.lazy(() => import('./components/Tools'));
const AutoWriter = React.lazy(() => import('./components/AutoWriter'));
const Agent = React.lazy(() => import('./components/Agent'));
const Competitor = React.lazy(() => import('./components/Competitor'));
const KeywordResearch = React.lazy(() => import('./components/KeywordResearch'));
const Wallet = React.lazy(() => import('./components/Wallet'));
const AdminDashboard = React.lazy(() => import('./components/AdminDashboard'));
const Library = React.lazy(() => import('./components/Library'));
const PublicView = React.lazy(() => import('./components/PublicView'));
const GscAnalytics = React.lazy(() => import('./components/GscAnalytics'));
const WebsiteManager = React.lazy(() => import('./components/WebsiteManager'));
const ContentPlanner = React.lazy(() => import('./components/ContentPlanner'));
const BrandVoiceManager = React.lazy(() => import('./components/BrandVoiceManager'));
import SeoPerformanceSummary from './components/SeoPerformanceSummary';

const Dashboard: React.FC<{ setView: (view: View) => void }> = ({ setView }) => {
  const { profile, user } = useAuth();
  const { error: toastError } = useToast();
  const [docCount, setDocCount] = useState(0);
  const [siteCount, setSiteCount] = useState(0);
  const [news, setNews] = useState<SEONewsItem[]>([]);
  const [loadingNews, setLoadingNews] = useState(false);

  useEffect(() => {
    if (!user) return;
    const unsubDocs = db.collection('documents').where('userId', '==', user.uid).onSnapshot((snap: any) => setDocCount(snap.size));
    const unsubSites = db.collection('users').doc(user.uid).collection('websites').onSnapshot((snap: any) => setSiteCount(snap.size));
    
    fetchNews();

    return () => { unsubDocs(); unsubSites(); };
  }, [user]);

  const fetchNews = async () => {
      if (!user) return;
      setLoadingNews(true);
      try {
          const result = await fetchRealtimeSEONews();
          setNews(result);
      } catch (e) {
          toastError("Lỗi tải tin tức SEO từ AI.");
      } finally {
          setLoadingNews(false);
      }
  };

  const StatItem = ({ label, value, icon: Icon, onClick, color }: any) => (
      <div onClick={onClick} className="bg-white p-6 rounded-2xl border border-stone-100 shadow-card hover:shadow-premium transition-all cursor-pointer group relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity"><Icon size={64}/></div>
          <div className="relative z-10">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${color} bg-opacity-10 text-opacity-100`}>
                  <Icon size={20} className={color.replace('bg-', 'text-')}/>
              </div>
              <div className="text-3xl font-bold text-stone-900 tracking-tight mb-1">{value}</div>
              <div className="text-xs font-semibold text-stone-500 uppercase tracking-wide">{label}</div>
          </div>
      </div>
  );

  return (
    <div className="p-6 lg:p-10 w-full mx-auto space-y-8 animate-fade-in overflow-y-auto h-full custom-scrollbar">
      
      <div className="flex flex-col sm:flex-row justify-between sm:items-end gap-4 border-b border-stone-200 pb-6">
          <div>
              <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-2xl font-bold text-stone-900 tracking-tight">Chào buổi sáng, {profile?.displayName || 'Creator'}!</h2>
                  <span className="text-[10px] bg-brand-50 text-brand-700 font-bold px-2.5 py-0.5 rounded-full border border-brand-200/60">Pure Content OS</span>
              </div>
              <p className="text-sm text-stone-500">Hệ điều hành nội dung chuẩn SEO E-E-A-T & Quản trị vòng đời bài viết tự động.</p>
          </div>
          <div className="text-xs font-medium text-stone-400 bg-stone-100 px-3 py-1.5 rounded-lg flex items-center gap-2 self-start sm:self-auto">
              <LayoutGrid size={14}/> {new Date().toLocaleDateString('vi-VN', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
      </div>

      {/* 4 Core Pillars Quick Action Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div onClick={() => setView(View.CONTENT_PLANNER)} className="p-5 rounded-2xl bg-white border border-stone-200/80 shadow-xs hover:shadow-md hover:border-brand-300 transition-all cursor-pointer group">
              <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <Target size={20}/>
              </div>
              <h4 className="text-sm font-bold text-stone-900 group-hover:text-brand-600 transition-colors">1. Strategy & Planner</h4>
              <p className="text-xs text-stone-500 mt-1 line-clamp-2">Topic Clusters, Nghiên cứu từ khóa & Phân tích Content Gap đối thủ.</p>
          </div>

          <div onClick={() => setView(View.WRITER)} className="p-5 rounded-2xl bg-white border border-stone-200/80 shadow-xs hover:shadow-md hover:border-brand-300 transition-all cursor-pointer group">
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <PenTool size={20}/>
              </div>
              <h4 className="text-sm font-bold text-stone-900 group-hover:text-brand-600 transition-colors">2. Content Studio</h4>
              <p className="text-xs text-stone-500 mt-1 line-clamp-2">Soạn thảo Co-Pilot tương tác cao & Chiến dịch Auto-Pilot hàng loạt.</p>
          </div>

          <div onClick={() => setView(View.LIBRARY)} className="p-5 rounded-2xl bg-white border border-stone-200/80 shadow-xs hover:shadow-md hover:border-brand-300 transition-all cursor-pointer group">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <HardDrive size={20}/>
              </div>
              <h4 className="text-sm font-bold text-stone-900 group-hover:text-brand-600 transition-colors">3. Lifecycle & Library</h4>
              <p className="text-xs text-stone-500 mt-1 line-clamp-2">Thư viện bài viết, cảnh báo Decay & Search Console Matrix.</p>
          </div>

          <div onClick={() => setView(View.BRAND_VOICE)} className="p-5 rounded-2xl bg-white border border-stone-200/80 shadow-xs hover:shadow-md hover:border-brand-300 transition-all cursor-pointer group">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <BrainCircuit size={20}/>
              </div>
              <h4 className="text-sm font-bold text-stone-900 group-hover:text-brand-600 transition-colors">4. The Brain & Connections</h4>
              <p className="text-xs text-stone-500 mt-1 line-clamp-2">Huấn luyện Brand Voice, tài liệu doanh nghiệp & CMS WordPress.</p>
          </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatItem label="Credits Khả dụng" value={profile?.credits || 0} icon={WalletIcon} onClick={() => setView(View.WALLET)} color="bg-brand-500 text-brand-600" />
          <StatItem label="Bài viết Lưu trữ" value={docCount} icon={HardDrive} onClick={() => setView(View.LIBRARY)} color="bg-blue-500 text-blue-600" />
          <StatItem label="CMS Websites Kết nối" value={siteCount} icon={Globe} onClick={() => setView(View.BRAND_VOICE)} color="bg-purple-500 text-purple-600" />
          <StatItem label="SEO Health Index" value="96/100" icon={Activity} onClick={() => setView(View.LIBRARY)} color="bg-emerald-500 text-emerald-600" />
      </div>

      {/* SEO Performance Summary Card (Google Search Console + Rankings Trend) */}
      <SeoPerformanceSummary setView={setView} />

      <div className="bg-white rounded-3xl border border-stone-200 shadow-sm p-8">
          <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-stone-900 flex items-center gap-3">
                  <BrainCircuit size={24} className="text-brand-500"/>
                  Tin tức SEO mới nhất
              </h3>
              <button onClick={fetchNews} disabled={loadingNews} className="p-2 text-stone-500 hover:text-stone-800 hover:bg-stone-100 rounded-xl transition-colors">
                  <RefreshCw size={18} className={loadingNews ? 'animate-spin' : ''} />
              </button>
          </div>

          {loadingNews ? (
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="p-6 rounded-2xl bg-stone-50 border border-stone-100 animate-pulse">
                      <div className="flex items-center gap-3 mb-3"><div className="w-8 h-8 rounded-full bg-stone-200"></div> <div className="h-4 w-32 bg-stone-200 rounded"></div></div>
                      <div className="h-3 w-full bg-stone-200 rounded mb-1"></div>
                      <div className="h-3 w-3/4 bg-stone-200 rounded"></div>
                      <div className="h-8 w-28 bg-stone-200 rounded-lg mt-4"></div>
                  </div>
                ))}
             </div>
          ) : news.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {news.map((item, index) => (
                      <div key={index} className="p-6 rounded-2xl bg-stone-50 border border-stone-200/60 hover:shadow-lg hover:border-brand-200 transition-all flex flex-col group">
                          <div className="flex items-center gap-3 mb-3">
                              <div className="w-9 h-9 flex items-center justify-center bg-white rounded-full shadow-sm border border-stone-100">
                                <TrendingUp size={20} className="text-blue-500"/>
                              </div>
                              <h4 className="font-bold text-stone-800 text-base">{item.title}</h4>
                          </div>
                          <p className="text-sm text-stone-500 leading-relaxed flex-1 mb-4">{item.summary}</p>
                          <a href={item.source} target="_blank" rel="noopener noreferrer" className="self-start flex items-center gap-2 bg-stone-900 text-white px-5 py-2 rounded-xl font-bold text-sm hover:bg-black transition-all shadow-lg active:scale-95 group-hover:bg-brand-600">
                              <span>Đọc thêm</span>
                              <ChevronRight size={16}/>
                          </a>
                      </div>
                  ))}
              </div>
          ) : (
              <div className="text-center py-12 text-stone-400">
                  <BrainCircuit size={48} className="mx-auto mb-4 opacity-10"/>
                  <p className="font-bold">Không có tin tức nào vào lúc này.</p>
                  <p className="text-sm mt-1">Vui lòng thử lại sau.</p>
              </div>
          )}
      </div>
    </div>
  );
};

const AppContent: React.FC = () => {
  const { user, loading, isAdmin } = useAuth();
  const [currentView, setCurrentView] = useState<View>(View.DASHBOARD);
  const [shareId, setShareId] = useState<string | null>(null);

  useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      const sid = params.get('shareId');
      if (sid) setShareId(sid);
  }, []);

  if (shareId) {
      return (
          <Suspense fallback={<div className="h-screen w-screen flex items-center justify-center bg-stone-50"><Loader2 className="animate-spin text-stone-400" size={32}/></div>}>
              <PublicView shareId={shareId} />
          </Suspense>
      );
  }

  if (loading) return <div className="h-screen w-screen flex items-center justify-center bg-stone-50"><Loader2 className="animate-spin text-stone-400" size={32}/></div>;
  if (!user) return <Login />;

  const renderView = () => {
    switch (currentView) {
      case View.DASHBOARD: return <Dashboard setView={setCurrentView} />;
      case View.CONTENT_PLANNER: return <ContentPlanner setView={setCurrentView} />;
      case View.BRAND_VOICE: return <BrandVoiceManager />;
      case View.KEYWORD_RESEARCH: return <KeywordResearch setView={setCurrentView} />;
      case View.COMPETITOR: return <Competitor defaultTab="skyscraper" setView={setCurrentView} />;
      case View.CONTENT_GAP: return <Competitor defaultTab="content-gap" setView={setCurrentView} />;
      case View.GSC_ANALYTICS: return <GscAnalytics setView={setCurrentView} />;
      case View.WRITER: return <Writer />;
      case View.AGENT: return <Agent setView={setCurrentView} />;
      case View.AUTO_WRITER: return <AutoWriter />;
      case View.AUDIT: return <Audit />;
      case View.TOOLS: return <Tools setView={setCurrentView} />;
      case View.LIBRARY: return <Library setView={setCurrentView} />;
      case View.WEBSITES: return <WebsiteManager />;
      case View.WALLET: return <Wallet />;
      case View.ADMIN_DASHBOARD: return isAdmin ? <AdminDashboard /> : <Dashboard setView={setCurrentView} />;
      default: return <Dashboard setView={setCurrentView} />;
    }
  };

  return (
    <Layout currentView={currentView} setCurrentView={setCurrentView}>
        <Suspense fallback={<div className="h-full w-full flex items-center justify-center min-h-[60vh] bg-stone-50"><Loader2 className="animate-spin text-stone-400" size={32}/></div>}>
           <div className="h-full animate-fade-in">
               {renderView()}
           </div>
        </Suspense>
    </Layout>
  );
};

const App: React.FC = () => {
  return (
    <ToastProvider>
        <AuthProvider>
           <QueueProvider>
              <AppContent />
           </QueueProvider>
        </AuthProvider>
    </ToastProvider>
  );
};

export default App;